import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  SortingState,
  PaginationState,
} from '@tanstack/react-table';
import { SearchIcon, FileText } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DataTable, DataTableHandle } from '@/components/ui/DataTable';
import {
  ColumnManagerPopover,
  ManagedColumn,
} from '@/components/ui/ColumnManagerPopover';
import { DateRangeFilter } from '@/components/ui/DateRangeFilter';
import { exportRows } from '@/components/ui/csvExport';
import { ExportCsvButton } from '@/components/ui/ExportCsvButton';
import { StatusFilterSelect } from '@/components/ui/StatusFilterSelect';
import { TableStateGate } from '@/components/ui/TableStateGate';
import { fetchStudies, useStudiesList } from '@/hooks/studies/useStudiesList';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { useDateRangeColumnFilters } from '@/hooks/useDateRangeColumnFilters';
import { useDebounce } from '@/hooks/useDebounce';
import { useFilterUrlSync } from '@/hooks/useFilterUrlSync';
import { useUser } from '@/providers/UserProvider';
import { StudyData } from '@/types/study';
import { getEntityStatusInfo } from '@/utils/entityStatuses';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ViewDetailsButton } from '@/components/ui/ViewDetailsButton';
import { formatTableDate } from '@/utils/dates';
import StudyDrawer from './components/StudyDrawer';

// Column-id -> backend `sort` param, for the columns whose server sort key
// isn't just the column id (the corpus-backed attribute sorts). Every other
// sortable column's backend key equals its column id (see
// Studies::SearchService::SORT_EXPRESSIONS).
const SORT_KEY_MAP: Record<string, string> = {
  pi_name: 'sort_pi',
  sponsor_name: 'sort_sponsor',
};

const DEFAULT_PAGE_SIZE = 25;

const TYPE_OPTIONS = [
  { value: 'Multi-Site Protocol', label: 'Multi-site' },
  { value: 'Single Site Protocol', label: 'Single site' },
];

const isMultiSite = (study: StudyData): boolean =>
  study.submission_template?.variant?.toLowerCase().includes('multi') ?? false;

// OV-1374: category alone is a valid label. OV2-imported templates can carry a
// sub_category that is just a Mongo id, which SubmissionTemplate#
// display_sub_category deliberately cleans away to nil — requiring both halves
// dropped those studies all the way to the protocol-number fallback, which is
// what the Type column was wrongly showing. Protocol number stays the last
// resort for a study with no filing at all.
const studyTypeLabel = (study: StudyData): string => {
  const { category, sub_category: subCategory } =
    study.submission_template ?? {};
  if (category && subCategory) return `${category} - ${subCategory}`;
  if (category) return category;
  return study.protocol_number || study.protocol_title || '-';
};

const STUDIES_TABLE_ID = 'studies';

const STUDIES_DEFAULT_COLUMN_ORDER = [
  'display_id',
  'type',
  'protocol_number',
  'pi_name',
  'sponsor_name',
  'sites_count',
  'submitted_at',
  'initial_completed_date',
  'expiration_date',
  'status',
  'view',
];

const STUDIES_MANAGEABLE_COLUMNS = [
  { id: 'display_id', defaultVisible: true, defaultPinned: 'left' as const },
  { id: 'type', defaultVisible: false },
  { id: 'protocol_number', defaultVisible: true },
  { id: 'pi_name', defaultVisible: true },
  { id: 'sponsor_name', defaultVisible: true },
  { id: 'sites_count', defaultVisible: true },
  { id: 'submitted_at', defaultVisible: true },
  { id: 'initial_completed_date', defaultVisible: false },
  { id: 'expiration_date', defaultVisible: true },
  { id: 'status', defaultVisible: true },
];

const COLUMN_LABELS: Record<string, string> = {
  display_id: 'ID',
  type: 'Type',
  protocol_number: 'Protocol Number',
  pi_name: 'PI Name',
  sponsor_name: 'Sponsor Name',
  sites_count: '# Sites',
  submitted_at: 'Submitted',
  initial_completed_date: 'Initial Completed Date',
  expiration_date: 'Expiration',
  status: 'Status',
};

const MANAGED_COLUMNS: ManagedColumn[] = STUDIES_MANAGEABLE_COLUMNS.map(
  ({ id }) => ({ id, label: COLUMN_LABELS[id] })
);

// Columns with a DateRangeFilter — kept in one place so the URL sync (mount
// read + effect write) below doesn't drift from which columns actually have
// a filter UI wired up.
const DATE_RANGE_FILTER_COLUMNS = ['submitted_at', 'initial_completed_date'];

const Studies: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('search') || ''
  );
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get('status') || ''
  );
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const [selectedStudyId, setSelectedStudyId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const dataTableRef = useRef<DataTableHandle>(null);
  const [exportRowCount, setExportRowCount] = useState(0);
  const { userData } = useUser();
  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    resetToDefaults,
  } = useColumnPreferences({
    tableId: STUDIES_TABLE_ID,
    userId: userData?.id,
    columns: STUDIES_MANAGEABLE_COLUMNS,
    defaultOrder: STUDIES_DEFAULT_COLUMN_ORDER,
  });
  const {
    columnFilters,
    setColumnFilters,
    dateRangeValue,
    setDateRangeValue,
    dateRangeUrlParams,
  } = useDateRangeColumnFilters({
    dateRangeColumns: DATE_RANGE_FILTER_COLUMNS,
    searchParams,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  // A date-range filter only ever runs client-side (see the plan doc — moving
  // it server-side needs a timezone-boundary port this change doesn't take
  // on), so a table with one active falls back to fetching the full
  // search/status/type-filtered set and paginating/filtering it in the
  // browser, exactly as before this change. Otherwise every page turn is a
  // real request for just that page.
  const hasActiveDateRangeFilter = columnFilters.length > 0;
  const useServerPagination = !hasActiveDateRangeFilter;

  const sortParam = sorting[0]
    ? (SORT_KEY_MAP[sorting[0].id] ?? sorting[0].id)
    : undefined;
  const orderParam = sorting[0]?.desc ? 'desc' : 'asc';

  // Any change that alters which rows/order come back must restart pagination
  // at page 1 — otherwise a user on page 3 could land past the end of a newly
  // narrowed result set.
  React.useEffect(() => {
    setPagination(prev =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }, [debouncedSearch, statusFilter, typeFilter, sorting, columnFilters]);

  // Mirror filter state into the URL so a filtered list survives a refresh and
  // can be shared or bookmarked — the behaviour Submissions already had. These
  // values are read back out of searchParams on mount (above).
  useFilterUrlSync(setSearchParams, {
    search: debouncedSearch,
    status: statusFilter,
    type: typeFilter,
    ...dateRangeUrlParams,
  });

  const {
    data = [],
    isLoading,
    error,
    pagination: serverPaginationMeta,
  } = useStudiesList({
    search: debouncedSearch,
    status: statusFilter,
    type: typeFilter,
    ...(useServerPagination && {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
      sort: sortParam,
      order: orderParam,
    }),
  });

  const handleOpenDrawer = useCallback((studyId: string) => {
    setSelectedStudyId(studyId);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          header: t('studies.fields.id'),
          id: 'display_id',
          accessorFn: (row: StudyData) =>
            row.display_id || row.id.slice(0, 8).toUpperCase(),
          enableSorting: true,
          meta: { overflow: 'nowrap', maxWidth: 120 },
          cell: ({ getValue }: { getValue: () => string }) => (
            <span className="text-gray-900 font-normal">{getValue()}</span>
          ),
        },
        {
          header: t('studies.fields.type'),
          id: 'type',
          accessorFn: (row: StudyData) => studyTypeLabel(row),
          enableSorting: true,
          meta: {
            overflow: 'truncate',
            minWidth: 260,
            csvFormatter: (_value, study: StudyData) => {
              const label = studyTypeLabel(study);
              const variant = study.submission_template?.variant;
              if (!variant) return label;
              return `${label} (${isMultiSite(study) ? 'Multi-site' : 'Single site'})`;
            },
          },
          cell: ({ row }: { row: { original: StudyData } }) => {
            const study = row.original;
            const variant = study.submission_template?.variant;
            return (
              <div className="flex items-center gap-2">
                <span className="truncate">{studyTypeLabel(study)}</span>
                {variant && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-gray-100 shrink-0">
                    {isMultiSite(study) ? 'Multi-site' : 'Single site'}
                  </span>
                )}
              </div>
            );
          },
        },
        {
          header: t('studies.fields.protocol_number'),
          id: 'protocol_number',
          accessorFn: (row: StudyData) => row.protocol_number,
          enableSorting: true,
          meta: {
            overflow: 'nowrap',
            maxWidth: 140,
            csvFormatter: value => (value as string) || '-',
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{getValue() || '-'}</span>
          ),
        },
        {
          header: t('studies.fields.pi_name'),
          id: 'pi_name',
          // AC: PI Name is for Single-Site studies only — Multi-Site (and any
          // study without a PI) falls back to '-'.
          accessorFn: (row: StudyData) =>
            isMultiSite(row) ? null : row.pi_name,
          enableSorting: true,
          meta: {
            overflow: 'truncate',
            maxWidth: 180,
            csvFormatter: value => (value as string) || '-',
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{getValue() || '-'}</span>
          ),
        },
        {
          header: t('studies.fields.sponsor_name'),
          id: 'sponsor_name',
          accessorFn: (row: StudyData) => row.sponsor_name,
          enableSorting: true,
          meta: {
            overflow: 'truncate',
            maxWidth: 180,
            csvFormatter: value => (value as string) || '-',
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{getValue() || '-'}</span>
          ),
        },
        {
          header: t('studies.fields.sites_count'),
          id: 'sites_count',
          accessorFn: (row: StudyData) => row.sites_count,
          enableSorting: true,
          meta: {
            overflow: 'nowrap',
            maxWidth: 90,
            csvFormatter: value => String((value as number) ?? 1),
          },
          cell: ({ getValue }: { getValue: () => number | null }) => (
            <span className="text-gray-600">{getValue() ?? 1}</span>
          ),
        },
        {
          header: t('studies.fields.submitted'),
          id: 'submitted_at',
          accessorFn: (row: StudyData) => row.submitted_at,
          enableSorting: true,
          sortingFn: 'nullableDateSort' as const,
          filterFn: 'dateRange' as const,
          meta: {
            overflow: 'nowrap',
            maxWidth: 80,
            csvFormatter: value => formatTableDate(value as string | null),
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{formatTableDate(getValue())}</span>
          ),
        },
        {
          header: t('studies.fields.initial_completed_date'),
          id: 'initial_completed_date',
          accessorFn: (row: StudyData) => row.initial_completed_date,
          enableSorting: true,
          sortingFn: 'nullableDateSort' as const,
          filterFn: 'dateRange' as const,
          meta: {
            overflow: 'nowrap',
            maxWidth: 170,
            csvFormatter: value => formatTableDate(value as string | null),
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{formatTableDate(getValue())}</span>
          ),
        },
        {
          header: t('studies.fields.expiration'),
          id: 'expiration_date',
          accessorFn: (row: StudyData) => row.expiration_date,
          enableSorting: true,
          sortingFn: 'nullableDateSort' as const,
          meta: {
            overflow: 'nowrap',
            maxWidth: 80,
            csvFormatter: value => formatTableDate(value as string | null),
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <span className="text-gray-600">{formatTableDate(getValue())}</span>
          ),
        },
        {
          header: t('studies.fields.status'),
          id: 'status',
          accessorFn: (row: StudyData) => row.status,
          enableSorting: true,
          meta: {
            overflow: 'nowrap',
            maxWidth: 100,
            csvFormatter: value =>
              value ? getEntityStatusInfo(value as string).label : '-',
          },
          cell: ({ getValue }: { getValue: () => string | null }) => (
            <StatusBadge status={getValue()} variant="entity" />
          ),
        },
        {
          id: 'view',
          header: '',
          enableSorting: false,
          meta: { overflow: 'nowrap', exportable: false },
          cell: ({ row }: { row: { original: StudyData } }) => (
            <ViewDetailsButton
              onClick={() => handleOpenDrawer(row.original.id)}
            />
          ),
        },
      ] as ColumnDef<StudyData, unknown>[],
    [handleOpenDrawer, t]
  );

  // When server pagination is active, `data` only holds the current page —
  // DataTable's own exportCsv (which reads its in-memory row set) can no
  // longer produce "every filtered row". Fetch the full search/status/type/
  // sort-matching set (no page param) instead and build the CSV from that.
  const handleExportAll = useCallback(async () => {
    const { resources } = await fetchStudies({
      search: debouncedSearch,
      status: statusFilter,
      type: typeFilter,
      sort: sortParam,
      order: orderParam,
    });
    exportRows({
      rows: resources,
      columns,
      columnOrder,
      columnVisibility,
      filename: 'studies.csv',
    });
  }, [
    debouncedSearch,
    statusFilter,
    typeFilter,
    sortParam,
    orderParam,
    columns,
    columnOrder,
    columnVisibility,
  ]);

  const handleCreateSubmission = () => navigate('/studies/new');

  const handleRowClick = useCallback(
    (row: StudyData, e: React.MouseEvent | React.KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        window.open(`/studies/${row.id}`, '_blank');
        return;
      }
      navigate(`/studies/${row.id}`);
    },
    [navigate]
  );

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white sticky top-0 z-10">
        <h1 className="text-gray-900 flex items-center gap-3">
          <span className="font-semibold text-2xl">{t('studies.title')}</span>
        </h1>
        <Button
          onClick={handleCreateSubmission}
          className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors"
        >
          <FileText className="w-4 h-4" />
          <span>{t('studies.new')}</span>
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* Filter bar — padded */}
        <div className="px-8 pt-8 pb-6 flex items-center gap-4">
          <div className="w-80 relative">
            <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('studies.search_placeholder')}
              aria-label={t('studies.search_placeholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#300091]/20 bg-white text-sm"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <Select
            value={typeFilter || 'all'}
            onValueChange={v => setTypeFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="px-4 py-2 border border-[#e9eaeb] rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 bg-white h-auto w-auto gap-2 [&>svg]:opacity-100">
              <span>
                {typeFilter
                  ? (TYPE_OPTIONS.find(o => o.value === typeFilter)?.label ??
                    t('studies.filter.type_label'))
                  : t('studies.filter.type_label')}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('studies.filter.all_types')}
              </SelectItem>
              {TYPE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <StatusFilterSelect
            value={statusFilter}
            onChange={setStatusFilter}
            allStatusesLabel={t('studies.filter.all_statuses')}
          />
          <DateRangeFilter
            label={t('studies.fields.submitted')}
            value={dateRangeValue('submitted_at')}
            onChange={setDateRangeValue('submitted_at')}
          />
          <DateRangeFilter
            label={t('studies.fields.initial_completed_date')}
            value={dateRangeValue('initial_completed_date')}
            onChange={setDateRangeValue('initial_completed_date')}
          />
          <div className="ml-auto flex items-center gap-2">
            <ExportCsvButton
              dataTableRef={dataTableRef}
              filename="studies.csv"
              rowCount={exportRowCount}
              onExport={useServerPagination ? handleExportAll : undefined}
            />
            <ColumnManagerPopover
              columns={MANAGED_COLUMNS}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              columnOrder={columnOrder}
              onColumnOrderChange={setColumnOrder}
              columnPinning={columnPinning}
              onColumnPinningChange={setColumnPinning}
              onReset={resetToDefaults}
            />
          </div>
        </div>

        {/* Table — inset with mx-8 so it can scroll horizontally */}
        <TableStateGate
          isLoading={isLoading}
          error={error}
          isEmpty={data.length === 0}
          errorMessage={t('studies.errors.failed_to_load')}
          emptyMessage={t('studies.empty_yet')}
        >
          <div className="mx-8 bg-white border border-[#e9eaeb] rounded-lg overflow-hidden">
            <DataTable
              ref={dataTableRef}
              columns={columns}
              data={data}
              onRowClick={handleRowClick}
              emptyMessage={t('studies.empty_yet')}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              columnOrder={columnOrder}
              onColumnOrderChange={setColumnOrder}
              columnPinning={columnPinning}
              onColumnPinningChange={setColumnPinning}
              columnFilters={columnFilters}
              onColumnFiltersChange={setColumnFilters}
              sorting={sorting}
              onSortingChange={setSorting}
              pagination={{
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
                onPaginationChange: setPagination,
                ...(useServerPagination && {
                  server: {
                    pageCount: serverPaginationMeta?.total_pages ?? 0,
                    rowCount: serverPaginationMeta?.total ?? 0,
                  },
                }),
              }}
              onExportRowCountChange={setExportRowCount}
            />
          </div>
        </TableStateGate>
      </div>

      <StudyDrawer
        studyId={selectedStudyId}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
      />
    </div>
  );
};

export default Studies;
